/**
 * useOnlineStatus Hook
 *
 * A custom hook to detect and track the browser's online/offline status.
 * Uses the Navigator.onLine property and listens for online/offline events.
 *
 * Features:
 * - Detects initial online/offline state
 * - Listens for state changes via window events
 * - Returns a boolean indicating current connectivity status
 * - Automatically cleans up event listeners on unmount
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const isOnline = useOnlineStatus();
 *
 *   if (!isOnline) {
 *     return <OfflineBanner />;
 *   }
 *
 *   return <MyContent />;
 * }
 * ```
 */

import { useState, useEffect, useCallback } from "react";

// ============================================================================
// Types
// ============================================================================

export interface UseOnlineStatusOptions {
  /**
   * Callback fired when the connection status changes.
   * Receives the new online status as a boolean.
   */
  onStatusChange?: (isOnline: boolean) => void;
}

export interface UseOnlineStatusReturn {
  /** Whether the browser is currently online */
  isOnline: boolean;
  /** Whether the browser is currently offline */
  isOffline: boolean;
  /**
   * Timestamp of when the current status was detected.
   * Useful for tracking how long the user has been offline.
   */
  statusChangedAt: Date | null;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if the browser is currently online.
 * Falls back to true if navigator.onLine is not available.
 */
function getOnlineStatus(): boolean {
  if (typeof navigator !== "undefined" && typeof navigator.onLine === "boolean") {
    return navigator.onLine;
  }
  // Default to online if we can't detect
  return true;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to detect browser online/offline status.
 *
 * @param options - Optional configuration including status change callback
 * @returns Object containing isOnline, isOffline, and statusChangedAt
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { isOnline, isOffline } = useOnlineStatus();
 *
 * // With callback
 * const { isOnline } = useOnlineStatus({
 *   onStatusChange: (online) => {
 *     if (online) {
 *       toast.success("Back online!");
 *     } else {
 *       toast.error("Connection lost");
 *     }
 *   },
 * });
 * ```
 */
export function useOnlineStatus(
  options: UseOnlineStatusOptions = {}
): UseOnlineStatusReturn {
  const { onStatusChange } = options;

  const [isOnline, setIsOnline] = useState<boolean>(getOnlineStatus);
  const [statusChangedAt, setStatusChangedAt] = useState<Date | null>(null);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setStatusChangedAt(new Date());
    onStatusChange?.(true);
  }, [onStatusChange]);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setStatusChangedAt(new Date());
    onStatusChange?.(false);
  }, [onStatusChange]);

  useEffect(() => {
    // Set initial status
    const initialStatus = getOnlineStatus();
    setIsOnline(initialStatus);

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return {
    isOnline,
    isOffline: !isOnline,
    statusChangedAt,
  };
}

// ============================================================================
// Convenience Exports
// ============================================================================

/**
 * Simple hook that returns just the online boolean.
 * Use when you only need the basic online/offline state.
 */
export function useIsOnline(): boolean {
  const { isOnline } = useOnlineStatus();
  return isOnline;
}

/**
 * Simple hook that returns just the offline boolean.
 * Use when you primarily need to check for offline state.
 */
export function useIsOffline(): boolean {
  const { isOffline } = useOnlineStatus();
  return isOffline;
}

export default useOnlineStatus;
